/**
 * Cash Requirements page — per-vehicle drawdown projections for leveraged funds.
 *
 * Drawdown model: 3 equal annual installments anchored to the first actual
 * drawdown date. Distributions can be toggled on to show net cash position.
 * Simulated funds from the Dashboard forecast tab can be included via toggle.
 */
import { useMemo, useState } from 'react'
import { useAuth } from './Auth/AuthContext'
import { usePortfolioData } from '../hooks/usePortfolioData'
import { useSimulationSettings } from '../hooks/useSimulationSettings'
import useIsMobile from '../hooks/useIsMobile'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts'
import {
  DEFAULT_EQT_PERF_RATE, DEFAULT_HOLDING_PERIOD, DEFAULT_BANK_INTEREST_RATE,
  EMPLOYEE_DRAWDOWN_PAYMENTS,
  makeKey, getDistAmount, getFundDrawdowns, calcCapitalStack, simulateLeveragedFund,
} from '../lib/fundCalculations'

const NUM_DRAWDOWNS = EMPLOYEE_DRAWDOWN_PAYMENTS

const formatCurrency = (value, decimals = 0) => {
  if (value == null) return '$0'
  const abs = Math.abs(value)
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  return value < 0 ? `-$${formatted}` : `$${formatted}`
}

// ── Main Component ──

export default function CashRequirements() {
  const { currentOrg, orgError, refreshOrgs } = useAuth()
  const orgId = currentOrg?.id
  const { data, fundVariables, fxData, loading, error, refresh } = usePortfolioData(orgId)
  const { settings: simSettings } = useSimulationSettings(orgId)
  const isMobile = useIsMobile()

  const [showDistributions, setShowDistributions] = useState(false)
  const [includeSimulations, setIncludeSimulations] = useState(false)

  const currentRate = fxData?.current_rate ?? 1.0

  // ── Compute drawdown schedule + distribution forecast ──
  const { vehicleData, summaryByYear, allYears } = useMemo(() => {
    if (!data?.funds || !data?.drawdowns) return { vehicleData: [], summaryByYear: {}, allYears: [] }

    const toUsd = (amount, currency) => currency === 'EUR' ? amount * currentRate : amount
    const currentYear = new Date().getFullYear()

    // Build fund table with leveraged status
    const fundEntries = []
    data.funds.forEach((f) => {
      const key = makeKey(f.fund, f.investor)
      const v = fundVariables?.[key]
      const isLev = v ? v['Leveraged?'] === 'Yes' : f.fund.includes('Leveraged')
      if (!isLev) return

      const commitment = f.total_commitment || 0
      const currency = f.currency || 'EUR'

      // First drawdown date for this fund/vehicle
      const fundDds = getFundDrawdowns(data.drawdowns, f.fund, f.investor)
      const firstDrawdownYear = fundDds.length > 0
        ? parseInt(fundDds[0].date.substring(0, 4))
        : currentYear

      // Actual drawn amount
      const totalDrawn = fundDds.reduce((s, d) => s + (d.amount || d.net_drawdown || 0), 0)

      // Distribution forecast
      const totalExposure = (v && Number(v['Total Exposure'])) ? Number(v['Total Exposure']) : commitment / 0.15
      const { participantAmt, sebAmt } = calcCapitalStack(totalExposure, commitment, true)
      const targetMoic = (v && Number(v['Target Fund MOIC']) > 0) ? Number(v['Target Fund MOIC']) : null
      const eqtPerfRate = DEFAULT_EQT_PERF_RATE
      const bankInterestRate = (v && Number(v['Avg Interest Rate (%)']) > 0) ? Number(v['Avg Interest Rate (%)']) / 100 : DEFAULT_BANK_INTEREST_RATE / 100
      const avgHoldingPeriod = (v && Number(v['Holding Period']) > 0) ? Number(v['Holding Period']) : DEFAULT_HOLDING_PERIOD
      const firstInvestmentYear = (v && Number(v['First Investment Year']) > 0) ? Number(v['First Investment Year']) : firstDrawdownYear - 1

      let simDistributionsByYear = {}
      if (targetMoic) {
        const result = simulateLeveragedFund({
          totalExposure, sebAmt, eqtPerfRate, bankInterestRate, firstInvestmentYear,
          vintageYear: firstDrawdownYear, avgHoldingPeriod,
          effectiveFundMoic: targetMoic, participantAmt,
        })
        // Scale for already-distributed amounts
        const fundDists = (data.distributions || [])
          .filter(d => d.fund === f.fund && d.investor === f.investor)
        const actualDist = fundDists.reduce((s, d) => s + getDistAmount(d), 0)
        const totalSimDist = Object.values(result.simDistributionsByYear).reduce((s, v) => s + v, 0)
        const remainingPayout = Math.max(0, result.lpNetReturn - actualDist)
        const scale = totalSimDist > 0 ? remainingPayout / totalSimDist : 0
        Object.entries(result.simDistributionsByYear).forEach(([yr, amt]) => {
          const y = Number(yr)
          if (y >= currentYear) simDistributionsByYear[y] = amt * scale
        })
      }

      fundEntries.push({
        fund: f.fund, investor: f.investor, currency, commitment,
        firstDrawdownYear, totalDrawn, isSimulated: false,
        simDistributionsByYear,
      })
    })

    // Add simulated funds if toggled on
    if (includeSimulations && simSettings?.simulated_funds) {
      simSettings.simulated_funds.forEach((sf) => {
        const commitment = Math.max(0, Number(sf.commitment) || 0)
        if (commitment <= 0) return
        const firstDrawdownYear = parseInt(sf.firstDrawdownYear, 10) || currentYear
        const participantPct = Number(sf.participantPct) || 15
        const totalExposure = participantPct > 0 ? commitment / (participantPct / 100) : commitment
        const { participantAmt, sebAmt } = calcCapitalStack(totalExposure, commitment, true)
        const targetMoic = Number(sf.targetMoic) || 0

        let simDistributionsByYear = {}
        if (targetMoic > 0) {
          const firstInvestmentYear = firstDrawdownYear - 1
          const result = simulateLeveragedFund({
            totalExposure, sebAmt, eqtPerfRate: DEFAULT_EQT_PERF_RATE,
            bankInterestRate: DEFAULT_BANK_INTEREST_RATE / 100,
            firstInvestmentYear, vintageYear: firstDrawdownYear,
            avgHoldingPeriod: DEFAULT_HOLDING_PERIOD,
            effectiveFundMoic: targetMoic, participantAmt,
          })
          Object.entries(result.simDistributionsByYear).forEach(([yr, amt]) => {
            const y = Number(yr)
            if (y >= currentYear) simDistributionsByYear[y] = amt
          })
        }

        fundEntries.push({
          fund: (sf.fundName || '').trim() || 'Simulated Fund',
          investor: 'Simulation',
          currency: 'USD',
          commitment,
          firstDrawdownYear,
          totalDrawn: 0,
          isSimulated: true,
          simDistributionsByYear,
        })
      })
    }

    // Build per-fund drawdown schedules
    const scheduleRows = [] // flat list of all rows
    fundEntries.forEach((fe) => {
      const perPayment = fe.commitment / NUM_DRAWDOWNS
      let remainingToProject = Math.max(0, fe.commitment - fe.totalDrawn)

      for (let i = 0; i < NUM_DRAWDOWNS; i++) {
        const yr = fe.firstDrawdownYear + i
        const isActual = !fe.isSimulated && fe.totalDrawn > 0 && yr < new Date().getFullYear()

        let amount
        if (fe.isSimulated) {
          amount = perPayment
        } else {
          if (yr <= currentYear && fe.totalDrawn >= perPayment * (i + 1)) {
            amount = perPayment
          } else if (yr <= currentYear && i === 0 && fe.totalDrawn > 0) {
            amount = Math.min(fe.totalDrawn, perPayment)
          } else if (remainingToProject > 0) {
            amount = Math.min(remainingToProject, perPayment)
          } else {
            amount = 0
          }
        }

        if (amount <= 0 && !isActual) continue

        const status = fe.isSimulated ? 'simulated' : (yr < currentYear ? 'actual' : 'projected')
        const nativeAmount = amount
        const usdAmount = toUsd(amount, fe.currency)

        scheduleRows.push({
          fund: fe.fund,
          investor: fe.investor,
          currency: fe.currency,
          year: yr,
          nativeAmount,
          usdAmount,
          status,
          isSimulated: fe.isSimulated,
        })

        if (status !== 'actual') remainingToProject -= amount
      }

      // Add distribution rows (only used in chart aggregation)
      if (showDistributions) {
        Object.entries(fe.simDistributionsByYear).forEach(([yr, amt]) => {
          const usdAmt = toUsd(amt, fe.currency)
          scheduleRows.push({
            fund: fe.fund,
            investor: fe.investor,
            currency: fe.currency,
            year: Number(yr),
            nativeAmount: 0,
            usdAmount: 0,
            distributionNative: amt,
            distributionUsd: usdAmt,
            status: 'distribution',
            isSimulated: fe.isSimulated,
          })
        })
      }
    })

    // Group by vehicle
    const byVehicle = {}
    scheduleRows.forEach((row) => {
      if (!byVehicle[row.investor]) {
        byVehicle[row.investor] = { name: row.investor, rows: [], isSimulated: row.isSimulated }
      }
      byVehicle[row.investor].rows.push(row)
    })

    // Build chart data per vehicle (by year) and summary
    const summaryByYear = {}
    const vehicleData = Object.values(byVehicle).map((v) => {
      const yearMap = {}
      v.rows.forEach((r) => {
        if (!yearMap[r.year]) yearMap[r.year] = { year: r.year, drawdowns: 0, distributions: 0 }
        if (r.status === 'distribution') {
          yearMap[r.year].distributions += r.distributionUsd || 0
        } else {
          yearMap[r.year].drawdowns += r.usdAmount || 0
        }

        if (!summaryByYear[r.year]) summaryByYear[r.year] = { year: r.year, drawdowns: 0, distributions: 0 }
        if (r.status === 'distribution') {
          summaryByYear[r.year].distributions += r.distributionUsd || 0
        } else {
          summaryByYear[r.year].drawdowns += r.usdAmount || 0
        }
      })

      const chartData = Object.values(yearMap).sort((a, b) => a.year - b.year)
      const drawdownRows = v.rows.filter(r => r.status !== 'distribution')
      const totalDrawdownsUsd = drawdownRows
        .filter(r => r.status !== 'actual')
        .reduce((s, r) => s + r.usdAmount, 0)

      return {
        name: v.name,
        isSimulated: v.isSimulated,
        rows: drawdownRows.sort((a, b) => a.year - b.year || a.fund.localeCompare(b.fund)),
        chartData,
        totalDrawdownsUsd,
      }
    })

    // Sort: real vehicles first, then simulation
    vehicleData.sort((a, b) => {
      if (a.isSimulated !== b.isSimulated) return a.isSimulated ? 1 : -1
      return a.name.localeCompare(b.name)
    })

    const allYears = [...new Set(scheduleRows.map(r => r.year))].sort()

    return { vehicleData, summaryByYear, allYears }
  }, [data, fundVariables, simSettings, includeSimulations, showDistributions, currentRate])

  // ── Summary chart data ──
  const summaryChartData = useMemo(() => {
    return allYears.map(yr => ({
      year: yr,
      Drawdowns: -(summaryByYear[yr]?.drawdowns || 0),
      ...(showDistributions ? { Distributions: summaryByYear[yr]?.distributions || 0 } : {}),
    }))
  }, [summaryByYear, allYears, showDistributions])

  const totalFutureDrawdownsUsd = vehicleData.reduce((s, v) => s + v.totalDrawdownsUsd, 0)
  const totalFutureDistributionsUsd = showDistributions
    ? Object.values(summaryByYear).reduce((s, y) => s + y.distributions, 0)
    : 0

  // ── Loading / Error / Empty states ──
  if (orgError) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="text-center max-w-md">
          <div className="text-warning-fg text-xl mb-2">Could not load organization</div>
          <div className="text-ink-subtle text-sm mb-4">{orgError}</div>
          <button onClick={refreshOrgs} className="px-4 py-2 bg-brand hover:opacity-90 text-ink-onAccent rounded-lg text-sm transition">Retry</button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <div className="text-ink-muted">Loading cash requirements...</div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="text-center max-w-md">
          <div className="text-danger-fg text-xl mb-2">Failed to load data</div>
          <div className="text-ink-subtle text-sm mb-4">{error}</div>
          <button onClick={refresh} className="px-4 py-2 bg-brand hover:opacity-90 text-ink-onAccent rounded-lg text-sm transition">Retry</button>
        </div>
      </div>
    )
  }

  if (!data?.funds || data.funds.length === 0) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-60px)]">
        <div className="text-center max-w-md">
          <div className="text-ink-muted text-xl mb-2">No portfolio data yet</div>
          <div className="text-ink-subtle text-sm">Import your data to see cash requirements.</div>
        </div>
      </div>
    )
  }

  const chartHeight = isMobile ? 220 : 300

  return (
    <div className="stack-lg">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-ink">Cash Requirements</h1>
          <p className="text-sm text-ink-muted mt-1">
            Projected drawdown schedule for leveraged funds, per vehicle.
            {fxData && currentRate !== 1 && (
              <span className="ml-1 text-ink-subtle">EUR/USD: {currentRate.toFixed(4)}</span>
            )}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Distributions toggle */}
          <div className="inline-flex items-center gap-1 rounded-lg border border-edge bg-surface-tertiary/80 p-1">
            <button
              onClick={() => setShowDistributions(false)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${!showDistributions ? 'bg-brand text-ink-onAccent shadow-sm' : 'text-ink-muted hover:text-ink'}`}
            >
              Drawdowns Only
            </button>
            <button
              onClick={() => setShowDistributions(true)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${showDistributions ? 'bg-brand text-ink-onAccent shadow-sm' : 'text-ink-muted hover:text-ink'}`}
            >
              Net of Distributions
            </button>
          </div>

          {/* Simulations toggle */}
          {simSettings?.simulated_funds?.length > 0 && (
            <div className="inline-flex items-center gap-1 rounded-lg border border-edge bg-surface-tertiary/80 p-1">
              <button
                onClick={() => setIncludeSimulations(false)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${!includeSimulations ? 'bg-brand text-ink-onAccent shadow-sm' : 'text-ink-muted hover:text-ink'}`}
              >
                Real Only
              </button>
              <button
                onClick={() => setIncludeSimulations(true)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${includeSimulations ? 'bg-brand text-ink-onAccent shadow-sm' : 'text-ink-muted hover:text-ink'}`}
              >
                + Simulations
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Summary KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-surface-secondary rounded-lg p-4 border border-edge-subtle">
          <div className="text-xs text-ink-muted mb-1">Remaining Drawdowns (USD)</div>
          <div className="text-xl font-bold text-ink">{formatCurrency(totalFutureDrawdownsUsd)}</div>
        </div>
        {showDistributions && (
          <div className="bg-surface-secondary rounded-lg p-4 border border-edge-subtle">
            <div className="text-xs text-ink-muted mb-1">Expected Distributions (USD)</div>
            <div className="text-xl font-bold text-success-fg">{formatCurrency(totalFutureDistributionsUsd)}</div>
          </div>
        )}
        <div className="bg-surface-secondary rounded-lg p-4 border border-edge-subtle">
          <div className="text-xs text-ink-muted mb-1">
            {showDistributions ? 'Net Cash Need (USD)' : 'Vehicles'}
          </div>
          <div className="text-xl font-bold text-ink">
            {showDistributions
              ? formatCurrency(totalFutureDrawdownsUsd - totalFutureDistributionsUsd)
              : vehicleData.length}
          </div>
        </div>
      </div>

      {/* Summary Chart */}
      {summaryChartData.length > 0 && (
        <div className="bg-surface-secondary rounded-lg p-4 sm:p-6 border border-edge-subtle">
          <h2 className="text-lg font-semibold text-ink mb-1">Aggregate Cash Requirements by Year</h2>
          <p className="text-xs text-ink-muted mb-4">All vehicles combined (USD)</p>
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={summaryChartData} stackOffset="sign">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
              <XAxis dataKey="year" stroke="var(--chart-axis)" style={{ fontSize: '0.75rem' }} />
              <YAxis stroke="var(--chart-axis)" style={{ fontSize: '0.75rem' }} tickFormatter={(v) => `$${(Math.abs(v) / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--chart-tooltip-bg)', border: '1px solid var(--chart-grid)', borderRadius: '0.5rem' }}
                formatter={(value) => [formatCurrency(Math.abs(value)), value < 0 ? 'Drawdowns' : 'Distributions']}
              />
              <Legend />
              <ReferenceLine y={0} stroke="var(--chart-axis)" />
              <Bar dataKey="Drawdowns" fill="var(--chart-negative)" radius={[0, 0, 4, 4]} />
              {showDistributions && <Bar dataKey="Distributions" fill="var(--chart-positive)" radius={[4, 4, 0, 0]} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per-Vehicle Sections */}
      {vehicleData.map((vehicle) => (
        <VehicleCard
          key={vehicle.name}
          vehicle={vehicle}
          showDistributions={showDistributions}
          chartHeight={isMobile ? 180 : 250}
        />
      ))}

      {vehicleData.length === 0 && (
        <div className="bg-surface-secondary rounded-lg p-8 border border-edge-subtle text-center">
          <div className="text-ink-muted">No leveraged funds found. This page shows cash requirements for leveraged funds only.</div>
        </div>
      )}
    </div>
  )
}

// ── Vehicle Card ──

function VehicleCard({ vehicle, showDistributions, chartHeight }) {
  const chartData = vehicle.chartData.map(d => ({
    year: d.year,
    Drawdowns: -d.drawdowns,
    ...(showDistributions ? { Distributions: d.distributions } : {}),
  }))

  const statusBadge = (status) => {
    const classes = {
      actual: 'bg-surface-tertiary text-ink-muted',
      projected: 'bg-brand/10 text-brand',
      simulated: 'bg-warning-bg text-warning-fg',
    }
    return (
      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${classes[status] || classes.projected}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  return (
    <div className="bg-surface-secondary rounded-lg border border-edge-subtle overflow-hidden">
      <div className="p-4 sm:p-6 border-b border-edge-subtle">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-ink">
              {vehicle.name}
              {vehicle.isSimulated && <span className="ml-2 text-xs text-warning-fg font-normal">(Simulated)</span>}
            </h3>
            <p className="text-sm text-ink-muted mt-0.5">
              Remaining drawdowns: {formatCurrency(vehicle.totalDrawdownsUsd)}
            </p>
          </div>
        </div>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="p-4 sm:px-6 sm:pb-2 border-b border-edge-subtle">
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={chartData} stackOffset="sign">
              <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
              <XAxis dataKey="year" stroke="var(--chart-axis)" style={{ fontSize: '0.75rem' }} />
              <YAxis stroke="var(--chart-axis)" style={{ fontSize: '0.7rem' }} tickFormatter={(v) => `$${(Math.abs(v) / 1000).toFixed(0)}k`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--chart-tooltip-bg)', border: '1px solid var(--chart-grid)', borderRadius: '0.5rem' }}
                formatter={(value) => [formatCurrency(Math.abs(value))]}
              />
              <ReferenceLine y={0} stroke="var(--chart-axis)" />
              <Bar dataKey="Drawdowns" fill="var(--chart-negative)" radius={[0, 0, 4, 4]} />
              {showDistributions && <Bar dataKey="Distributions" fill="var(--chart-positive)" radius={[4, 4, 0, 0]} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-edge">
              <th className="text-left py-3 px-4 text-ink-muted font-medium">Year</th>
              <th className="text-left py-3 px-4 text-ink-muted font-medium">Fund</th>
              <th className="text-right py-3 px-4 text-ink-muted font-medium">Native Amt</th>
              <th className="text-right py-3 px-4 text-ink-muted font-medium">USD Amt</th>
              <th className="text-center py-3 px-4 text-ink-muted font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {vehicle.rows.map((row, idx) => (
              <tr key={`${row.fund}-${row.year}-${idx}`} className="border-b border-edge-subtle hover:bg-surface-secondary/50">
                <td className="py-2.5 px-4 text-ink">{row.year}</td>
                <td className="py-2.5 px-4 text-ink truncate max-w-[200px]" title={row.fund}>{row.fund}</td>
                <td className="py-2.5 px-4 text-ink text-right font-mono text-xs">
                  {row.currency === 'EUR' ? '€' : '$'}{Math.round(row.nativeAmount).toLocaleString()}
                </td>
                <td className="py-2.5 px-4 text-ink text-right font-mono text-xs">
                  {formatCurrency(row.usdAmount)}
                </td>
                <td className="py-2.5 px-4 text-center">{statusBadge(row.status)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
